import type { ColorPickerProps } from '@wix/design-system';
import { Box, ColorPicker as ColorPickerBase } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext } from 'react';

import { ModalServiceContext, RicosContext } from './../../../../../ricos-context';
import {
  Panel,
  PanelHeader,
  useClickOutside,
  usePanelContainerElement,
} from './../../../..';
import { CUSTOM_COLOR_PICKER } from './../../../../color-picker/new-color-picker/data-hooks';

import { CUSTOM_COLOR_PICKER_MODAL_ID } from '../../constants';
import classes from './custom-color-picker-scss';

interface CustomColorPickerProps {
  currentColor: string | undefined;
  customColors: string[];
  onColorPreviewStart?: (color: string | undefined) => void;
  onColorPreviewEnd?: () => void;
  onColorAdded: (color: string) => void;
  referenceElement: HTMLDivElement | null;
}

const getColorHex = (color: ColorPickerProps['value']) =>
  typeof color === 'string' ? color : color.hex();

export const CustomColorPicker: FC<CustomColorPickerProps> = ({
  currentColor,
  customColors,
  onColorPreviewStart,
  onColorPreviewEnd,
  onColorAdded,
  referenceElement,
}) => {
  const { t, isMobile } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext);

  const containerEl = usePanelContainerElement();

  const colorValue = currentColor?.toUpperCase() ?? '';

  const closeModal = () => modalService.closeModal(CUSTOM_COLOR_PICKER_MODAL_ID);

  const onCloseButtonClick = () => {
    onColorPreviewEnd?.();
    closeModal();
  };

  useClickOutside({
    containerRef: { current: containerEl },
    onClickOutside: (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null;

      if (
        target?.dataset?.hook === 'tooltip-text' ||
        target?.role === 'tooltip' ||
        containerEl.contains(target as Node) ||
        referenceElement?.contains(target as Node)
      ) {
        return;
      }

      onCloseButtonClick();
    },
  });

  return (
    <Panel
      onCloseButtonClick={onCloseButtonClick}
      dataHook={CUSTOM_COLOR_PICKER}
      width={isMobile ? '100%' : 288}
      maxHeight={isMobile ? undefined : 555}
      height="100%"
    >
      <PanelHeader title={t('Color_Picker_CustomColor_Panel_Title')} className={classes.header} />
      <Box
        direction="vertical"
        paddingTop="SP3"
        paddingLeft="SP3"
        paddingRight="SP3"
        paddingBottom="SP2"
      >
        <Box align="center" className={classes.customPicker}>
          <ColorPickerBase
            allowEmpty
            value={colorValue}
            showConverter={false}
            onChange={newColor => {
              const color = getColorHex(newColor);
              onColorPreviewStart?.(color);
            }}
            onConfirm={newColor => {
              const color = getColorHex(newColor);
              if (!customColors.includes(color)) {
                onColorAdded(color);
              }
              closeModal();
            }}
            onCancel={onCloseButtonClick}
            emptyPlaceholder={t('CustomColorPicker_Empty_Placeholder')}
          />
        </Box>
      </Box>
    </Panel>
  );
};
